import React, { type JSX, useContext } from "react";
import { type MatrixEvent, MatrixError, type IPreviewUrlResponse, type MatrixClient } from "matrix-js-sdk/src/matrix";
import { logger } from "matrix-js-sdk/src/logger";
import CloseIcon from "@vector-im/compound-design-tokens/assets/web/icons/close";

import { useStateToggle } from "../../../hooks/useStateToggle";
import LinkPreviewWidget from "./LinkPreviewWidget";
import AccessibleButton from "../elements/AccessibleButton";
import { _t } from "../../../languageHandler";
import MatrixClientContext from "../../../contexts/MatrixClientContext";
import { useAsyncMemo } from "../../../hooks/useAsyncMemo";
import { useMediaVisible } from "../../../hooks/useMediaVisible";
import axios from "axios";

const INITIAL_NUM_PREVIEWS = 2;

interface IProps {
    links: string[]; // the URLs to be previewed
    mxEvent: MatrixEvent; // the Event associated with the preview
    onCancelClick(): void; // called when the preview's cancel ('hide') button is clicked
}

const LinkPreviewGroup: React.FC<IProps> = ({ links, mxEvent, onCancelClick }) => {
    const cli = useContext(MatrixClientContext);
    const [expanded, toggleExpanded] = useStateToggle();
    const [mediaVisible] = useMediaVisible(mxEvent);

    const ts = mxEvent.getTs();
    const previews = useAsyncMemo<[string, IPreviewUrlResponse][]>(
        async () => {
            return fetchPreviews(cli, links, ts);
            // return fetchPreviews(links);
        },
        [links, ts],
        [],
    );

    const showPreviews = expanded ? previews : previews.slice(0, INITIAL_NUM_PREVIEWS);

    let toggleButton: JSX.Element | undefined;
    if (previews.length > INITIAL_NUM_PREVIEWS) {
        toggleButton = (
            <AccessibleButton onClick={toggleExpanded}>
                {expanded
                    ? _t("action.collapse")
                    : _t("timeline.url_preview.show_n_more", { count: previews.length - showPreviews.length })}
            </AccessibleButton>
        );
    }

    return (
        <div className="mx_LinkPreviewGroup">
            {showPreviews.map(([link, preview], i) => (
                <LinkPreviewWidget
                    mediaVisible={mediaVisible}
                    key={link}
                    link={link}
                    preview={preview}
                    mxEvent={mxEvent}
                >
                    {i === 0 ? (
                        <AccessibleButton
                            className="mx_LinkPreviewGroup_hide"
                            onClick={onCancelClick}
                            aria-label={_t("timeline.url_preview.close")}
                        >
                            <CloseIcon width="20px" height="20px" />
                        </AccessibleButton>
                    ) : undefined}
                </LinkPreviewWidget>
            ))}
            {toggleButton}
        </div>
    );
};

const fetchPreviews = (cli: MatrixClient, links: string[], ts: number): Promise<[string, IPreviewUrlResponse][]> => {
    return Promise.all<[string, IPreviewUrlResponse] | void>(
        links.map(async (link): Promise<[string, IPreviewUrlResponse] | undefined> => {
            try {
                let preview: IPreviewUrlResponse | null;
                let exc: any;
                try {
                    // Prelievo l'anteprima del link utilizzando il metodo getUrlPreview del client Matrix
                    preview = await cli.getUrlPreview(link, ts);
                } catch (error) {
                    exc = error;
                    // Se il metodo getUrlPreview del client Matrix fallisce, proviamo a prelevare l'anteprima del link utilizzando un servizio esterno
                    // ATTENZIONE: FUNZIONA SOLO IN LOCALHOST, IN PRODUZIONE IL SERVIZIO ESTERNO NON FUNZIONA 
                    // A CAUSA DI PROBLEMI DI CORS, PRIVACY E SICUREZZA, 
                    // QUINDI PER ORA ABBIAMO DECISO DI NON ABILITARE LA FUNZIONALITÀ DI ANTEPRIMA DEI LINK
                    preview = await getOpenGraphData(link);
                }

                if (!preview) throw exc;

                // Ensure at least one of the rendered fields is truthy
                if (
                    preview?.["og:image"]?.startsWith("mxc://") ||
                    preview?.["og:image"]?.startsWith("http://") ||
                    preview?.["og:image"]?.startsWith("https://") ||
                    !!preview?.["og:description"] ||
                    !!preview?.["og:title"]
                ) {
                    return [link, preview];
                }
            } catch (error) {
                if (error instanceof MatrixError && error.httpStatus === 404) {
                    // Quieten 404 Not found errors, not all URLs can have a preview generated
                    logger.debug("Failed to get URL preview: ", error);
                } else {
                    logger.error("Failed to get URL preview: ", error);
                }
            }
        }),
    ).then((a) => a.filter(Boolean)) as Promise<[string, IPreviewUrlResponse][]>;
};

//#region PRELIEVO ANTEPRIMA IMMAGINE DA SERVIZIO ESTERNO
// Funzione per ottenere i metadati Open Graph da un URL
const getOpenGraphData = async (url: string): Promise<IPreviewUrlResponse | null> => {
    try {
        // Chiamata all'API Microlink
        const response = await axios.get(`https://api.microlink.io/?url=${encodeURIComponent(url)}`);

        // Estrarre direttamente i metadati da Microlink
        const data = response.data.data; // I dati sono in 'data' in formato JSON
        console.error("metadata data: " + JSON.stringify(data));

        if (!data) {
            console.error('No meta data found');
            return null;
        }

        const imageInfo = data.logo ?? data.image ?? undefined;
        const metadata: IPreviewUrlResponse = {
            "og:title": data.title || '',
            "og:type": data.type || '',
            "og:url": data.url || '',
            "og:image": imageInfo?.url,
            "og:image:type": imageInfo?.type || undefined,
            "og:image:height": parseInt(imageInfo?.height?.toString() || '0'),
            "og:image:width": parseInt(imageInfo?.width?.toString() || '0'),
            "og:description": data.description || undefined,
            "matrix:image:size": parseInt(imageInfo?.size?.toString() || '0')
        };

        console.error("metadata: " + JSON.stringify(metadata));

        return metadata;
    } catch (error) {
        console.error('Error fetching Open Graph data:', error);
        return null;
    }
};
//#endregion

export default LinkPreviewGroup;
